import { useState, useRef, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";

// ** Redux
import { useDispatch, useSelector } from "react-redux";
import { getUser } from "store/modules/global-user/actions/get-user.action";

// ** Third Party
import { useTranslation } from "react-i18next";
import ReactCrop from "react-image-crop";
import { window } from "ssr-window";
import { toast } from "helpers/toastWrapper";

import "react-image-crop/dist/ReactCrop.css";

/** COMPONENTS */
import { ButtonPrimary } from "components/layout/Button/ButtonPrimary";
import MediaList from "./components/MediaList";
import Block from "components/layout/Block";
import Paragraph from "components/typography/Paragraph";
import Tooltip from "components/layout/Tooltip";
import { StyledOverlay } from "styles/components/Overlay.styled";
import Loader, { StyledLoaderWrapper } from "components/layout/Loader";

/** SYNC USER */
import syncReduxWithLocalstorage from "libs/sync-redux-with-localstorage";

/** HELPERS */
import api from "helpers/ApiClient";
import getMediaFromAvatar from "helpers/getMediaFromAvatar";
import cdnPrefixer from "helpers/cdnPrefixer";

// #####################################################

const pixelRatio = window.devicePixelRatio || 1;
const DEFAULT_POSITION = 0;

// #####################################################

const Avatar = () => {
	const dispatch = useDispatch();

	const user = useSelector((state) => state.globalUser);
	const suspension = useSelector((state) => state.globalUser.suspension);
	const { t } = useTranslation(["userpanel", "suspensions"]);
	const isBan = suspension?.duration > 0;
	const tooltipInfo = isBan ? t("suspensions:bannedAccount") : "";

	const _aspect = 1 / 1;

	const [crop, setCrop] = useState({
		unit: "%",
		width: 50,
		aspect: _aspect,
		x: 0,
		y: 0,
	});

	const [whileChanging, setWhileChangine] = useState(false);
	const [currentAvatar, setCurrentAvatar] = useState(null);
	const [completedCrop, setCompletedCrop] = useState(null);
	const [coords, setCoords] = useState(null);
	const [firstLoad, setFirstLoad] = useState(true);
	const [photoLoading, setPhotoLoading] = useState(true);
	const [hashMedia, setHashMedia] = useState(null);
	const [list, setList] = useState({
		loading: false,
		success: false,
		data: [],
		currentPage: 1,
		totalPages: 1,
		total: 0,
	});

	const imgRef = useRef(null);
	const previewCanvasRef = useRef(null);

	const isDarkMode = useSelector(
		(state) => state.globalCommon?.darkMode?.isDarkMode
	);

	const location = useLocation();
	const history = useHistory();

	// If you setState the crop in here you should return false.
	const onImageLoaded = (image) => {
		imgRef.current = image;

		if (image.width < 100 || image.height < 100) {
			setCurrentAvatar(null);
			toast.error(t("photos.avatar.badFormat"));
		}
	};

	const onCropChange = (crop) => {
		setCrop(crop);
	};

	const handleChangeAvatar = () => {
		setWhileChangine(true);
		const { x, y, width, height } = coords;

		if (currentAvatar.media_type === "video") {
			const coordinates = {
				video: currentAvatar.id,
				x1: Math.floor(x),
				x2: Math.floor(x + width),
				y1: Math.floor(y),
				y2: Math.floor(y + height),
			};

			api.put("/video/avatar", coordinates)
				.then(() => {
					toast.success(t("photos.avatar.success"));
					setWhileChangine(false);
				})
				.catch(() => {
					toast.error(t("photos.avatar.error"));
					setWhileChangine(false);
				});
		} else {
			const coordinates = {
				photo: currentAvatar.id,
				x1: Math.floor(x),
				x2: Math.floor(x + width),
				y1: Math.floor(y),
				y2: Math.floor(y + height),
			};

			api.put("/photo/avatar", coordinates)
				.then(() => {
					toast.success(t("photos.avatar.success"));
					setWhileChangine(false);
					dispatch(getUser({ setUser: true }));
					syncReduxWithLocalstorage({
						globalUser: true,
					});
				})
				.catch(() => {
					toast.error(t("photos.avatar.error"));
					setWhileChangine(false);
				});
		}
	};

	const getCurrentPhoto = async () => {
		const { avatar: id, type } = getMediaFromAvatar(user.avatar);

		try {
			const { data } = await (type === "video"
				? api.get(`/video?id=${id}`)
				: api.get(`/photo?id=${id}`));

			const media = {
				id: data.id,
				media_type: type,
				media: {
					big: data?.images?.big,
					poster: data?.thumbs?.poster,
				},
				width: data.width,
				height: data.height,
			};

			setCurrentAvatar(media);
		} catch (err) {
			//
		}
	};

	// ** Funkcja pobierająca avatary z API
	const fetchData = async (page = 1) => {
		setList((prev) => ({
			...prev,
			loading: true,
		}));

		const { data } = await api.get(`media/avatar`, {
			params: {
				page,
				hash: hashMedia,
			},
		});

		setHashMedia(data?.hash);

		setList((prev) => {
			const prevData = prev?.data || [];
			const newPhotos = data?.media || [];
			const avatar = data?.avatar || null;
			return {
				loading: false,
				success: true,
				data: avatar
					? [avatar, ...prevData, ...newPhotos]
					: [...prevData, ...newPhotos],
				currentPage: data?.pagination?.currentpage || 1,
				totalPages: data?.pagination?.totalpages || 0,
				total: data?.total || 0,
			};
		});
	};

	const generatePreview = () => {
		let crop = completedCrop;

		if (firstLoad) {
			crop = centerPreview();
			setFirstLoad(false);
		}

		const image = imgRef.current;
		const canvas = previewCanvasRef.current;

		const scaleX = image.naturalWidth / image.width;
		const scaleY = image.naturalHeight / image.height;
		const ctx = canvas.getContext("2d");

		canvas.width = crop.width * pixelRatio;
		canvas.height = crop.height * pixelRatio;

		ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
		ctx.imageSmoothingQuality = "high";

		ctx.drawImage(
			image,
			crop.x * scaleX,
			crop.y * scaleY,
			crop.width * scaleX,
			crop.height * scaleY,
			0,
			0,
			crop.width,
			crop.height
		);

		setCoords({
			x: crop.x * scaleX,
			y: crop.y * scaleY,
			width: crop.width * scaleX,
			height: crop.height * scaleY,
		});
	};

	const centerPreview = () => {
		const image = imgRef.current;

		const width = image.width;
		const height = image.height;

		if (width > height) {
			const newCrop = {
				aspect: _aspect,
				unit: "px",
				width: height - DEFAULT_POSITION * 2,
				height: height - DEFAULT_POSITION * 2,
				x: width / 2 - (height - DEFAULT_POSITION * 2) / 2,
				y: DEFAULT_POSITION,
			};

			setCrop(newCrop);
			return newCrop;
		} else {
			const newCrop = {
				aspect: _aspect,
				unit: "px",
				width: width - DEFAULT_POSITION * 2,
				height: width - DEFAULT_POSITION * 2,
				x: DEFAULT_POSITION,
				y: height / 2 - (width - DEFAULT_POSITION * 2) / 2,
			};

			setCrop(newCrop);
			return newCrop;
		}
	};

	useEffect(() => {
		if (!location.state?.forceReload) {
			setPhotoLoading(true);
			fetchData();
			getCurrentPhoto().finally(() => {
				setPhotoLoading(false);
			});
		}

		return () => {
			setFirstLoad(true);
		};
	}, []);

	// ** Usunięteo useEffect - return
	useEffect(() => {
		if (completedCrop && previewCanvasRef.current && imgRef.current) {
			generatePreview();
		}
	}, [completedCrop]);

	useEffect(() => {
		if (location.state?.forceReload) {
			setPhotoLoading(true);
			getCurrentPhoto().finally(() => {
				setPhotoLoading(false);
			});
			history.replace({ ...location, state: null });
		}
	}, [location.state?.forceReload]);

	if (photoLoading) {
		return (
			<StyledLoaderWrapper>
				<Loader />
			</StyledLoaderWrapper>
		);
	}
	return (
		<div key={`avatar` + location.key}>
			<MediaList
				setCurrentAvatar={setCurrentAvatar}
				setFirstLoad={setFirstLoad}
				list={list}
				fetchData={(page) => {
					fetchData(page);
				}}
			/>
			{currentAvatar && (
				<Block>
					<Block relative flex column justifyCenter fullHeight mb={3}>
						<Paragraph mb={0} mt={0}>
							{t("photos.avatar.profilePhoto")}:
						</Paragraph>
					</Block>

					<Block mb={4} flex relative>
						<StyledOverlay style={{ zIndex: 3 }} />
						<canvas
							ref={previewCanvasRef}
							style={{
								width: "180px",
								height: "180px",
								borderRadius: "50%",
								outline: isDarkMode
									? "4px solid white"
									: "unset",
							}}
						/>
					</Block>
					<ReactCrop
						src={cdnPrefixer(
							currentAvatar.media_type === "video"
								? currentAvatar.media.poster
								: currentAvatar.media.big
						)}
						crop={crop}
						circularCrop
						keepSelection
						minWidth={100}
						minHeight={100}
						onImageLoaded={onImageLoaded}
						onComplete={setCompletedCrop}
						onChange={onCropChange}
					/>
				</Block>
			)}
			<Block justifyEnd flex mt={3}>
				<Tooltip
					text={tooltipInfo}
					wrapperStyle={{
						position: "relative",
						left: 0,
						right: 0,
						top: 0,
						bottom: 0,
					}}
				>
					<ButtonPrimary
						onClick={handleChangeAvatar}
						disabled={
							currentAvatar === null || isBan || whileChanging
						}
					>
						{t("common:save")}
					</ButtonPrimary>
				</Tooltip>
			</Block>
		</div>
	);
};

// #####################################################

export default Avatar;

// #####################################################
